import type { ManipulateType } from "dayjs";
import dayjs from "dayjs";
import duration from "dayjs/plugin/duration";
import relativeTime from "dayjs/plugin/relativeTime";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";

dayjs.extend(duration);
dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(relativeTime);

import { BONUSES_SCHEDULE } from "@/utils/constants";

export const DEFAULT_DATE_FORMAT = "MMM D[th] YYYY [at] h:mmA";
export const DEFAULT_POST_DATE_FORMAT = "MM/DD/YY @ hh:mma";
export const DEFAULT_POST_COMMENT_FORMAT = "MM/DD/YY";

type FocusDate = string | number | Date | dayjs.Dayjs | null | undefined;

export const FocusDateParser = {
  now() {
    return dayjs().toDate();
  },

  add(value: number, unit: ManipulateType, date?: FocusDate) {
    return dayjs(date).add(value, unit).toDate();
  },

  subtract(value: number, unit: ManipulateType, date?: FocusDate) {
    return dayjs(date).subtract(value, unit).toDate();
  },

  parse(date: FocusDate) {
    return dayjs(date).toDate();
  },

  format(date: FocusDate, formatTemplate: string) {
    return dayjs(date).format(formatTemplate);
  },

  getNextBonusDate(current: Date): Date | null {
    const dates = BONUSES_SCHEDULE.map(FocusDateParser.parse);

    // Filter dates that are in the future
    const futureDates = dates.filter((date) => {
      return date.getTime() > current.getTime();
    });

    // If there are future dates, find the closest one
    if (futureDates.length > 0) {
      const closestDate = futureDates.reduce((prev, curr) =>
        curr.getTime() < prev.getTime() ? curr : prev,
      );
      return closestDate;
    }

    // If there are no future dates, return null
    return null;
  },

  getDurationFromToday(selectedDateISOString: string): string {
    const selectedDate = new Date(selectedDateISOString);
    const currentDate = new Date();

    const timeDifference = selectedDate.getTime() - currentDate.getTime();
    const days = Math.floor(timeDifference / (1000 * 60 * 60 * 24));
    const hours = Math.floor(
      (timeDifference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60),
    );
    const minutes = Math.floor(
      (timeDifference % (1000 * 60 * 60)) / (1000 * 60),
    );

    const entities = [
      { value: days, unit: "day" },
      { value: hours, unit: "hour" },
      { value: minutes, unit: "minute" },
    ].filter(({ value }) => value > 0);

    let durationString = "";

    entities.forEach((entity, index) => {
      const { value, unit } = entity;

      if (index > 0) {
        durationString += index === entities.length - 1 ? " and " : ", ";
      }

      durationString += `${value} ${unit}${value === 1 ? "" : "s"}`;
    });

    return durationString.length > 0 ? durationString : "Just now";
  },

  getLocalUTCDate(utcDate: FocusDate) {
    return dayjs.utc(utcDate).local();
  },

  formatUTCDate(
    utcDate: FocusDate,
    format?: string,
    pendingStateLabel: string = "",
  ): string {
    if (pendingStateLabel && !utcDate) {
      return pendingStateLabel;
    }

    return FocusDateParser.getLocalUTCDate(utcDate).format(format);
  },

  formatUTCDateFromNow(utcDate: FocusDate) {
    return FocusDateParser.getLocalUTCDate(utcDate).fromNow();
  },

  formatUTCDateFromNowShort(
    utcDate: FocusDate,
    { forcePast = false }: { forcePast?: boolean },
  ) {
    let dateToFormat = utcDate;

    if (forcePast && utcDate) {
      const now = new Date();
      if (utcDate > now) {
        // Snap future timestamps to "now" to avoid awkward "in 1m"
        dateToFormat = now;
      }
    }

    const fullText = FocusDateParser.formatUTCDateFromNow(dateToFormat);

    const replacements = [
      // Past
      { regex: /a few seconds ago/, replacement: "just now" },
      { regex: /a minute ago/, replacement: "1m ago" },
      { regex: /an hour ago/, replacement: "1h ago" },
      { regex: /a day ago/, replacement: "1d ago" },
      { regex: /a month ago/, replacement: "1mo ago" },
      { regex: /a year ago/, replacement: "1y ago" },
      { regex: /(\d+)\s*seconds? ago/, replacement: "$1s ago" },
      { regex: /(\d+)\s*minutes? ago/, replacement: "$1m ago" },
      { regex: /(\d+)\s*hours? ago/, replacement: "$1h ago" },
      { regex: /(\d+)\s*days? ago/, replacement: "$1d ago" },
      { regex: /(\d+)\s*months? ago/, replacement: "$1mo ago" },
      { regex: /(\d+)\s*years? ago/, replacement: "$1y ago" },

      // Future
      { regex: /in a few seconds/, replacement: "soon" },
      { regex: /in a minute/, replacement: "in 1m" },
      { regex: /in an hour/, replacement: "in 1h" },
      { regex: /in a day/, replacement: "in 1d" },
      { regex: /in a month/, replacement: "in 1mo" },
      { regex: /in a year/, replacement: "in 1y" },
      { regex: /in (\d+)\s*seconds?/, replacement: "in $1s" },
      { regex: /in (\d+)\s*minutes?/, replacement: "in $1m" },
      { regex: /in (\d+)\s*hours?/, replacement: "in $1h" },
      { regex: /in (\d+)\s*days?/, replacement: "in $1d" },
      { regex: /in (\d+)\s*months?/, replacement: "in $1mo" },
      { regex: /in (\d+)\s*years?/, replacement: "in $1y" },
    ];

    const shortText = replacements.reduce((text, { regex, replacement }) => {
      return text.replace(regex, replacement);
    }, fullText);

    return shortText.trim();
  },

  formatEasternTime(date: FocusDate): string {
    return dayjs(date).tz("America/New_York").format(DEFAULT_DATE_FORMAT);
  },

  toNanoSeconds(amount: number, unit: dayjs.ManipulateType): number {
    const durationInMilliseconds = dayjs
      .duration(amount, unit)
      .asMilliseconds();
    const nanoSeconds = durationInMilliseconds * 1_000_000; // 1 millisecond = 1,000,000 nanoseconds
    return nanoSeconds;
  },

  fromNanoSeconds(nanoSeconds: number, unit: dayjs.ManipulateType): number {
    return dayjs.duration(nanoSeconds / 1_000_000, "milliseconds").as(unit);
  },

  toSeconds(amount: number, unit: dayjs.ManipulateType): number {
    return dayjs.duration(amount, unit).asSeconds();
  },

  getFutureTimeInNanoSecWithRate(xRate: number) {
    const oneYearInNanoSec = 31556952000000000;
    const currentTime = new Date().getTime() * 1e6;
    return (currentTime + oneYearInNanoSec) * xRate;
  },

  nanoSecondsToMilliseconds(nanoSeconds: number): number {
    return nanoSeconds / 1e6;
  },

  msToLocaleString(ms: number): string {
    return new Date(ms).toLocaleString(undefined, {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  },
};
